import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { enqueueSnackbar } from "notistack";
import { useFeedback } from "../../context/FeedbackContext";
import { useAuth } from "../../context/AuthContext";
import { submitFeedback } from "../../api/feedbackAPI";
import colors from "../../config/colors";

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FeedbackBugModal = () => {
  const { isOpen, feedbackType, feedbackContext, closeFeedbackModal } =
    useFeedback();
  const { user } = useAuth();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);

  const isBugReport = feedbackType === "bug";
  const isLoggedIn = Boolean(user?.data?.email);
  const showContactEmailField = !isLoggedIn;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setDescription("");
      setContactEmail(feedbackContext.user_email || user?.data?.email || "");
      setAttachments([]);
      setErrors({});
    }
  }, [isOpen, feedbackContext.user_email, user?.data?.email]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      enqueueSnackbar(`You can only upload up to ${MAX_ATTACHMENTS} files`, {
        variant: "warning",
      });
      return;
    }

    // Validate file sizes
    const validFiles = files.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`${file.name}: File size must be less than 5MB`, {
          variant: "warning",
        });
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));

    setAttachments((prev) =>
      [...prev, ...newAttachments].slice(0, MAX_ATTACHMENTS),
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const cleanupAttachments = () => {
    attachments.forEach((a) => URL.revokeObjectURL(a.preview));
    setAttachments([]);
  };

  const handleSubmit = async () => {
    // Validation
    const newErrors = {};
    if (!description.trim()) {
      newErrors.description = "Please add a short message";
    }
    if (!isBugReport && !title.trim()) {
      newErrors.title = "Please add a title";
    }
    const resolvedEmail = (user?.data?.email || contactEmail)
      .trim()
      .toLowerCase();
    if (!resolvedEmail) {
      newErrors.contactEmail = "Please enter your email address";
    } else if (!EMAIL_PATTERN.test(resolvedEmail)) {
      newErrors.contactEmail = "Please enter a valid email address";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const brandId =
        feedbackContext.brand_id ??
        user?.data?.parent_brand?.id ??
        user?.data?.brand?.id ??
        0;
      const brandName =
        feedbackContext.brand_name ??
        user?.data?.parent_brand?.name ??
        user?.data?.brand?.display_name ??
        user?.data?.brand?.name ??
        "";
      const resolvedEmail = (user?.data?.email || contactEmail)
        .trim()
        .toLowerCase();

      await submitFeedback({
        type: feedbackType,
        title: isBugReport ? "" : title,
        description,
        page_url: window.location.href,
        attachments: attachments.map((a) => a.file),
        brand_id: brandId,
        brand_name: brandName || resolvedEmail,
        user_id: user?.data?.id || 0,
        user_email: resolvedEmail,
        timestamp: new Date().toISOString(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
      });

      enqueueSnackbar(
        isBugReport
          ? "Bug has been reported successfully"
          : "Feedback has been submitted successfully",
        { variant: "success" },
      );

      handleClose();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Unable to submit, please try again",
        { variant: "error" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    cleanupAttachments();
    closeFeedbackModal();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "8px", maxWidth: "480px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isBugReport ? "Report a Bug" : "Submit Feedback"}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {showContactEmailField ? (
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Your email address
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="Enter email address"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  if (errors.contactEmail) {
                    setErrors({ ...errors, contactEmail: "" });
                  }
                }}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail}
                variant="outlined"
                size="small"
              />
            </Box>
          ) : null}

          {/* Title - Only for feedback */}
          {!isBugReport && (
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Feedback Title
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter title here"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors({ ...errors, title: "" });
                }}
                error={!!errors.title}
                helperText={errors.title}
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {/* Description */}
          <Box>
            <Typography variant="body2" fontWeight={500} mb={1}>
              {isBugReport ? "Bug details" : "Feedback details"}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter details here"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
              error={!!errors.description}
              helperText={errors.description}
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Attachments - Only for bug report */}
          {isBugReport && (
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Add attachment
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {/* Uploaded files */}
                {attachments.map((attachment, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      border: "1px solid",
                      borderColor: colors.gray100,
                      borderRadius: "4px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <img
                      src={attachment.preview}
                      alt={attachment.name}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {attachment.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={() => handleRemoveAttachment(index)}
                      sx={{
                        color: colors.primary500,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                      Remove
                    </Typography>
                  </Box>
                ))}

                {/* Upload button */}
                {attachments.length < MAX_ATTACHMENTS && (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 60,
                      height: 60,
                      border: "1px dashed",
                      borderColor: colors.gray100,
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "#fafafa",
                      "&:hover": {
                        borderColor: colors.gray200,
                      },
                    }}
                  >
                    <NoteAddOutlinedIcon sx={{ color: colors.gray200 }} />
                  </Box>
                )}
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
            </Box>
          )}

          {/* Submit Button */}
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={handleSubmit}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 44,
              backgroundColor: colors.primary500,
              color: colors.white,
              borderRadius: "4px",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: isSubmitting ? colors.primary500 : "#d94430",
              },
              "&:disabled": {
                backgroundColor: colors.primary500,
                color: colors.white,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : isBugReport ? (
              "Report a bug"
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackBugModal;
